/* archive-builder.js — main-thread client for the 7z-wasm archive BUILD worker.
 *
 * Owns three things the UI shouldn't have to:
 *   1. collecting a selection (files, folders, dropped directory trees) into a
 *      flat [{path, file, size, mtime}] list with sane, de-duplicated paths;
 *   2. the advisor — probing real bytes on THIS device to answer "which setting
 *      is actually worth it", instead of shipping a hardcoded speed table;
 *   3. worker lifecycle, including cancel (terminate = instant abort, because
 *      7-Zip's callMain is synchronous and cannot be interrupted any other way).
 *
 * Public: window.ArchiveBuilder.create() -> instance, plus statics
 * (collectFromDataTransfer, FORMATS, LEVELS, fmtBytes).
 */
(function () {
  'use strict';
  if (window.ArchiveBuilder) return;

  function vh(key) { var h = (window.__VENDOR_HASHES || {})[key]; return h ? ('?v=' + h) : ''; }
  /* Server-resolved: the blade emits __ARCHIVE_WORKER_URL via AssetRouter::worker(),
   * which picks the minified twin and appends ?v=filemtime. That version query is
   * not cosmetic — `new Worker()` does not go through the Service Worker, so a
   * hardcoded URL keeps serving pre-fix worker bytes from the browser's own HTTP
   * cache for up to 24 h after a deploy. Literal path stays as the dev fallback. */
  /* __ARCHIVE_BUILD_WORKER_URL wins over the legacy shared global — see the
   * matching note in archive-engine.js (both used to read one global with
   * different defaults; a page using both could not satisfy them). */
  var WORKER_URL = window.__ARCHIVE_BUILD_WORKER_URL || window.__ARCHIVE_WORKER_URL || '/js/archive/archive-build-worker.js';
  /* Worker-side helper modules. Their URLs are resolved by the SERVER (the
     blade emits them through AssetRouter::worker) and passed IN, because a
     relative `import` inside a module worker inherits no version and would sit
     in the browser's immutable cache for up to a year after a fix. */
  var OPFS_FS_URL = window.__ARCHIVE_OPFS_FS_URL || '/js/archive/opfs-out-fs.js';
  var HASH_URL = window.__ARCHIVE_HASH_URL || '/js/hash-stream.js';
  var JS_URL = '/vendor/7z-wasm/7zz.es6.js' + vh('sevenZipJs');
  var WASM_URL = '/vendor/7z-wasm/7zz.wasm' + vh('sevenZipWasm');

  // Formats we can actually produce, verified end-to-end on real fixtures.
  // zstd is deliberately absent: this 7-Zip build can READ .zst but `-tzstd`
  // is not a supported create type, and shipping a format that silently fails
  // is worse than not offering it.
  var FORMATS = [
    { id: 'zip',     ext: '.zip',     multi: true,  password: true,  encryptNames: false, split: true },
    { id: '7z',      ext: '.7z',      multi: true,  password: true,  encryptNames: true,  split: true },
    { id: 'tar',     ext: '.tar',     multi: true,  password: false, encryptNames: false, split: true },
    { id: 'tar.gz',  ext: '.tar.gz',  multi: true,  password: false, encryptNames: false, split: true },
    { id: 'tar.bz2', ext: '.tar.bz2', multi: true,  password: false, encryptNames: false, split: true },
    { id: 'tar.xz',  ext: '.tar.xz',  multi: true,  password: false, encryptNames: false, split: true },
    { id: 'gz',      ext: '.gz',      multi: false, password: false, encryptNames: false, split: false },
    { id: 'bz2',     ext: '.bz2',     multi: false, password: false, encryptNames: false, split: false },
    { id: 'xz',      ext: '.xz',      multi: false, password: false, encryptNames: false, split: false },
  ];
  var LEVELS = [0, 1, 5, 9];

  /* Mirrors the guard in archive-build-worker.js — see the comment there for the
   * measurements. Duplicated on this side so the UI can refuse a bad password
   * while the user is still typing rather than after a long build aborts. */
  var ZIP_PW_MAX = 99;
  function validatePassword(format, pw) {
    if (!pw || format !== 'zip') return null;
    if (!/^[\x20-\x7E]*$/.test(pw)) return 'non_ascii';
    if (pw.length > ZIP_PW_MAX) return 'too_long';
    return null;
  }

  function fmtBytes(n) {
    if (n == null || isNaN(n)) return '—';
    var u = ['B', 'KB', 'MB', 'GB', 'TB'], i = 0; n = Number(n);
    while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
    return (i === 0 ? Math.round(n) : n.toFixed(n < 10 ? 1 : 0)) + ' ' + u[i];
  }

  // ---------------------------------------------------------------- selection
  var JUNK_RE = /(^|\/)(\.DS_Store|Thumbs\.db|desktop\.ini|ehthumbs\.db|\.Spotlight-V100|\.Trashes|\.fseventsd)$/i;

  function normPath(p) {
    return String(p || '')
      .replace(/\\/g, '/')
      .replace(/^\/+/, '')
      .split('/')
      .filter(function (s) { return s && s !== '.' && s !== '..'; })   // no traversal into the archive
      .join('/');
  }

  function entryFor(file, relPath) {
    var path = normPath(relPath || file.webkitRelativePath || file.name);
    return { path: path, file: file, size: file.size, mtime: file.lastModified || Date.now() };
  }

  /* Drag-and-dropped folders only expose their contents through the
   * webkitGetAsEntry tree — `dataTransfer.files` flattens a dropped folder to
   * nothing at all in Chromium. Every browser archiver that reads only .files
   * silently ignores dropped folders, which is the single most common "why is
   * my archive empty" complaint about them. */
  async function collectFromDataTransfer(dt) {
    var items = dt && dt.items ? Array.prototype.slice.call(dt.items) : [];
    var entries = [];
    var roots = [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (it.kind !== 'file') continue;
      var ent = it.webkitGetAsEntry && it.webkitGetAsEntry();
      if (ent) roots.push(ent);
      else { var f = it.getAsFile(); if (f) entries.push(entryFor(f, f.name)); }
    }
    for (var j = 0; j < roots.length; j++) await walkEntry(roots[j], '', entries);
    if (!entries.length && dt && dt.files && dt.files.length) {
      for (var k = 0; k < dt.files.length; k++) entries.push(entryFor(dt.files[k], dt.files[k].name));
    }
    return entries;
  }

  /* The same drop, handed over by the page after the handler returned: the
   * layout's rescue for a folder dropped OFF the zone reads the entry tree
   * synchronously ({ dirs, loose }) and gives it to __acceptDroppedTree. Without
   * it that drop stopped at 500 files, 8 s and depth 6 and skipped empty files. */
  async function collectFromSnapshot(snap) {
    var entries = (snap && snap.loose || []).map(function (f) { return entryFor(f, f.name); });
    var dirs = (snap && snap.dirs) || [];
    for (var j = 0; j < dirs.length; j++) await walkEntry(dirs[j], '', entries);
    return entries;
  }

  function walkEntry(entry, prefix, acc) {
    return new Promise(function (resolve) {
      if (entry.isFile) {
        entry.file(function (f) { acc.push(entryFor(f, prefix + entry.name)); resolve(); }, function () { resolve(); });
        return;
      }
      if (!entry.isDirectory) { resolve(); return; }
      var reader = entry.createReader();
      var kids = [];
      var readBatch = function () {
        reader.readEntries(async function (batch) {
          if (!batch.length) {
            // readEntries returns at most 100 per call — loop until it is empty,
            // otherwise big folders lose everything past the first 100 entries.
            for (var i = 0; i < kids.length; i++) await walkEntry(kids[i], prefix + entry.name + '/', acc);
            resolve();
            return;
          }
          kids = kids.concat(Array.prototype.slice.call(batch));
          readBatch();
        }, function () { resolve(); });
      };
      readBatch();
    });
  }

  // Merge new entries into an existing list: skip exact duplicates, rename
  // colliding paths rather than silently dropping or overwriting them.
  function mergeEntries(existing, incoming) {
    var byPath = Object.create(null);
    for (var i = 0; i < existing.length; i++) byPath[existing[i].path] = existing[i];
    var added = 0, skipped = 0;
    for (var j = 0; j < incoming.length; j++) {
      var e = incoming[j];
      if (!e.path) { skipped++; continue; }
      var cur = byPath[e.path];
      if (cur) {
        if (cur.size === e.size && cur.mtime === e.mtime) { skipped++; continue; }
        var dot = e.path.lastIndexOf('.');
        var base = dot > 0 ? e.path.slice(0, dot) : e.path;
        var ext = dot > 0 ? e.path.slice(dot) : '';
        var n = 2;
        while (byPath[base + ' (' + n + ')' + ext]) n++;
        e.path = base + ' (' + n + ')' + ext;
      }
      byPath[e.path] = e;
      existing.push(e);
      added++;
    }
    return { added: added, skipped: skipped };
  }

  // Strip the longest common directory prefix so dropping one folder does not
  // bury everything under a redundant wrapper the user never asked for.
  function commonPrefix(entries) {
    if (entries.length < 2) return '';
    var parts = entries[0].path.split('/').slice(0, -1);
    for (var i = 1; i < entries.length && parts.length; i++) {
      var p = entries[i].path.split('/').slice(0, -1);
      var n = 0;
      while (n < parts.length && n < p.length && parts[n] === p[n]) n++;
      parts = parts.slice(0, n);
    }
    return parts.join('/');
  }

  // ------------------------------------------------------------------- engine
  function Builder() {
    this._worker = null;
    this._seq = 0;
    this._pending = Object.create(null);
    this._onProgress = null;
    this._mountedFor = null;
    this.entries = [];
    this.flatten = false;    // "don't keep folder structure" — see _wire()
    this.rootFolder = '';    // optional wrapper directory inside the archive
    this.target = null;      // existing archive being appended to (append mode)
    this.targetFile = null;
    this._targetPw = '';
  }

  Builder.prototype._ensure = function () {
    if (this._worker) return;
    var self = this;
    /* Firefox 68 (Sentry HG/HF) модульный воркер НЕ отвергает: он стартует и
       падает на первом `import` — SyntaxError в window.onerror, а человеку
       «worker_crash». Спрашиваем движок ДО создания; общий пробник в layout
       сам говорит человеку и пишет телеметрию, нам остаётся честно остановиться. */
    if (typeof window !== 'undefined' && window.__requireModuleWorker && !window.__requireModuleWorker()) {
      var tooOld = new Error('module_worker_unsupported'); tooOld.code = 'MODULE_WORKER_UNSUPPORTED';
      // Та же фраза, что сказал слой, — для страниц, которые пишут userMessage в строку файла (/file-converter).
      tooOld.userMessage = window.__requireModuleWorker.text || ''; throw tooOld;
    }
    this._worker = new Worker(WORKER_URL, { type: 'module' });
    this._worker.onmessage = function (ev) {
      var d = ev.data || {};
      if (d.stream === 'progress') { if (self._onProgress) self._onProgress(d); return; }
      var p = self._pending[d.id];
      if (!p) return;
      delete self._pending[d.id];
      if (d.ok) p.resolve(d.chunks ? { result: d.result, chunks: d.chunks } : d.result);
      else {
        var e = new Error((d.error && d.error.message) || 'worker_error');
        e.code = d.error && d.error.code;
        e.detail = d.error && d.error.detail;   // numbers the message needs (space, quota)
        p.reject(e);
      }
    };
    this._worker.onerror = function (ev) {
      // The tool takes the failure (every pending call rejects with worker_crash and the page
      // says so); uncancelled it would also reach window.onerror — the shared toast, Sentry.
      if (ev && ev.preventDefault) ev.preventDefault();
      var ids = Object.keys(self._pending);
      for (var i = 0; i < ids.length; i++) {
        self._pending[ids[i]].reject(new Error('worker_crash: ' + ((ev && ev.message) || '')));
        delete self._pending[ids[i]];
      }
    };
  };

  Builder.prototype._rpc = function (op, payload) {
    this._ensure();
    var self = this, id = ++this._seq;
    return new Promise(function (resolve, reject) {
      self._pending[id] = { resolve: resolve, reject: reject };
      self._worker.postMessage(Object.assign({ id: id, op: op, jsUrl: JS_URL, wasmUrl: WASM_URL,
        opfsUrl: OPFS_FS_URL, hashUrl: HASH_URL }, payload));
    });
  };

  // Entries are re-sent as {path,size,mtime} plus the File handle; File is
  // structured-cloneable and cheap (a handle, not the bytes).
  Builder.prototype._wire = function () {
    // Optional wrapper directory. Without it, an archive of loose files empties
    // itself into whatever folder the recipient extracts into — the "tar bomb"
    // everyone has been on the receiving end of at least once.
    var root = normPath(this.rootFolder || '');
    var wrap = function (p) { return root ? root + '/' + p : p; };
    if (!this.flatten) {
      return this.entries.map(function (e) { return { path: wrap(e.path), file: e.file, size: e.size, mtime: e.mtime }; });
    }
    // Flattening collapses every file to its basename, so two files from
    // different folders can collide. Renaming beats the alternatives: dropping
    // one loses data, and letting WORKERFS overwrite would put the same bytes
    // in twice under one name without ever saying so.
    var used = Object.create(null);
    return this.entries.map(function (e) {
      var base = e.path.split('/').pop();
      var name = base;
      if (used[name]) {
        var dot = base.lastIndexOf('.');
        var stem = dot > 0 ? base.slice(0, dot) : base;
        var ext = dot > 0 ? base.slice(dot) : '';
        var n = 2;
        while (used[stem + ' (' + n + ')' + ext]) n++;
        name = stem + ' (' + n + ')' + ext;
      }
      used[name] = 1;
      return { path: wrap(name), file: e.file, size: e.size, mtime: e.mtime };
    });
  };

  Builder.prototype.mount = async function () {
    var key = (this.flatten ? 'F:' : 'S:') + (this.rootFolder || '') + ':' + this.entries.length + ':'
      + this.entries.reduce(function (s, e) { return s + e.size; }, 0) + ':'
      + (this.entries[0] ? this.entries[0].path : '');
    if (this._mountedFor === key) return this._mountInfo;
    /* Re-entrancy, not just caching. `_mountedFor` is only set AFTER the RPC
       returns, so two callers arriving during that window (advise() and the
       build click are the pair that does it) both issued a mount — and a
       WORKERFS mountpoint cannot be mounted twice, so the second threw an
       Emscripten ErrnoError, which carries no `.message` and reached the UI as
       the string "[object Object]". Sharing the in-flight promise makes the
       second caller wait for the first instead of racing it. */
    if (this._mountingFor === key && this._mounting) return this._mounting;
    // A WORKERFS mount cannot be replaced in place; a fresh worker is the clean
    // way to re-mount after the selection changes, and it also frees the old
    // module's heap in one go.
    var self2 = this;
    this._mountingFor = key;
    this._mounting = (async function () {
      if (self2._mountedFor) await self2.reset();
      self2._mountInfo = await self2._rpc('mount', { entries: self2._wire() });
      self2._mountedFor = key;
      await self2._restoreTarget();
      return self2._mountInfo;
    })().finally(function () { self2._mounting = null; self2._mountingFor = null; });
    return this._mounting;
  };


  /* Probe candidate settings against a real sample of the user's own bytes.
   * Returns rows carrying BOTH the measured ratio and the measured throughput,
   * so estimates track this device instead of my laptop. */
  Builder.prototype.advise = async function () {
    await this.mount();
    var specs = [
      { id: 'zip-0', type: 'zip', level: 0 },
      { id: 'zip-1', type: 'zip', level: 1 },
      { id: 'zip-5', type: 'zip', level: 5 },
      { id: 'zip-9', type: 'zip', level: 9 },
      { id: '7z-5',  type: '7z',  level: 5 },
      { id: '7z-9',  type: '7z',  level: 9 },
    ];
    var res = await this._rpc('probe', { specs: specs, entries: this._wire() });
    var total = this.totalBytes();
    var rows = [];
    for (var i = 0; i < res.results.length; i++) {
      var r = res.results[i];
      if (!r.ok) continue;
      var parts = r.id.split('-');
      var lvl = parseInt(parts[1], 10);
      rows.push({
        id: r.id, format: parts[0], level: lvl,
        ratio: r.ratio,
        estBytes: Math.round(total * r.ratio),
        estSeconds: r.mbPerSec > 0 ? (total / 1048576) / r.mbPerSec * timeCorrection(this, r.id, r.dictRatio) : null,
        mbPerSec: r.mbPerSec,
        saved: 1 - r.ratio,
      });
    }
    this._lastProbe = {};
    for (var k = 0; k < rows.length; k++) this._lastProbe[rows[k].id] = rows[k].mbPerSec;
    return { rows: rows, sampleBytes: res.sampleBytes, recommendation: recommend(rows, total) };
  };

  /* A 1.5 MB head-sample cannot predict wall clock to better than ~2x, and the
   * bias is one-sided: the sample is small enough that 7-Zip gives it a small
   * dictionary and skips the per-file overhead the real run pays. Measured
   * est-vs-real across 8 corpus/setting combinations (2026-07-29) the real build
   * took 0.65x - 2.7x the raw extrapolation, median 1.9x. So the raw number is
   * scaled up, and the UI renders it as a rounded "about" figure rather than
   * false precision. Under-promising the wait is the kinder direction to err.
   *
   * Anything actually measured beats the prior: every completed build feeds its
   * real MB/s back in, so a second estimate on the same device is grounded.
   */
  var PROBE_TIME_BIAS = 1.7;

  /* Dictionary codecs need a second correction on top. probeDict() deliberately
   * shrinks the LZMA2 dictionary so the ratio stays honest, but a small
   * dictionary is also cheaper per byte, so the raw speed reading is optimistic
   * by an amount that grows with how far the dictionary was shrunk. Two ground
   * truths (2026-07-29, full builds timed against their own estimates):
   *
   *   8.9 MB of text   dictionary shrunk  18x   real took 1.6x the estimate
   *   2.08 GB of MP3   dictionary shrunk 1365x  real took 3.5x the estimate
   *
   * ln(18)=2.9 and ln(1365)=7.2, so the two points sit on 1 + 0.35*ln(shrink)
   * within a few percent. Anything actually measured still wins: the first
   * completed build overwrites this with the device's real throughput.
   */
  function dictTimeBias(dictRatio) {
    if (!dictRatio || dictRatio <= 1) return 1;
    return Math.min(6, 1 + 0.35 * Math.log(dictRatio));
  }

  function timeCorrection(builder, id, dictRatio) {
    var c = builder._calib && builder._calib[id];
    if (c) return c;
    return PROBE_TIME_BIAS * dictTimeBias(dictRatio);
  }

  /* The rule this tool exists to enforce.
   *
   * Measured on real corpora (see archive-build-worker.js header): on a folder
   * of JPEG/MP4/MP3, ZIP-Maximum spends 64 s to save 1.0% where ZIP-Fast spends
   * 5 s to save 0.6%, and 7z-Maximum spends 112 s for the same 1.0%. Every
   * competing tool happily lets you pick Maximum there. So:
   *   - if nothing saves more than 3%, the files are already compressed → store;
   *   - otherwise take the CHEAPEST setting that lands within 2 points of the
   *     best achievable saving, because the remainder is never worth the wait.
   */
  function recommend(rows, total) {
    if (!rows.length) return null;
    var best = rows.reduce(function (a, b) { return b.saved > a.saved ? b : a; });
    if (best.saved < 0.03) {
      var store = rows.filter(function (r) { return r.level === 0; })[0] || rows[0];
      return { id: store.id, reason: 'incompressible', bestSaved: best.saved, bestId: best.id };
    }
    var good = rows.filter(function (r) { return r.saved >= best.saved - 0.02; });
    good.sort(function (a, b) { return (a.estSeconds || 0) - (b.estSeconds || 0); });
    var pick = good[0];
    return {
      id: pick.id,
      reason: pick.id === best.id ? 'best' : 'diminishing',
      bestSaved: best.saved, bestId: best.id,
      bestSeconds: best.estSeconds, pickSeconds: pick.estSeconds,
    };
  }

  Builder.prototype.build = async function (spec, onProgress) {
    await this.mount();
    this._onProgress = onProgress || null;
    var total = this.totalBytes();
    /* The room the worker checks before the work — measured HERE, because the
       worker has no window: window.__storageEstimate (timbrica-core.js) asks the
       browser's real grant, which estimate() hides on most Chromium builds behind
       a static usage + 10 GiB (support chat 647). Measured up to the most any
       format can need at once — the archive plus, for tar.*, the uncompressed tar
       it is made from — so the worker's exact sum is always inside the answer. */
    try {
      if (window.__storageEstimate && spec && spec.mode !== 'append') {
        var upTo = Math.max(1, spec.expectBytes || total || 0) + (total || 0) + 64 * 1048576;
        var g = await window.__storageEstimate({ upTo: upTo });
        if (g) spec = Object.assign({}, spec, { room: { usage: g.usage || 0, quota: g.quota || 0, measured: !!g.measured, memCeiling: memoryCeiling() } });
      }
    } catch (e) { /* no answer → the worker simply does not pre-check */ }
    try {
      var out = await this._rpc('build', { spec: spec, entries: this._wire() });
      // Ground the next estimate in what this device actually did.
      if (this._lastProbe && out.result.ms > 0) {
        var id = spec.format + '-' + (spec.level == null ? 5 : spec.level);
        var probed = this._lastProbe[id];
        if (probed > 0) {
          var realMbps = (total / 1048576) / (out.result.ms / 1000);
          this._calib = this._calib || {};
          this._calib[id] = Math.max(0.3, Math.min(6, probed / realMbps));
        }
      }
      return out;   // {result, chunks:[{name, bytes:Uint8Array}]}
    } finally { this._onProgress = null; }
  };

  /* Load an existing archive to append to. Its bytes DO have to be copied into
   * the worker — appending writes to the archive, and a WORKERFS mount is
   * read-only — so this is the one place the tool spends memory on an input. */
  Builder.prototype.openTarget = async function (file, password) {
    var buf = await file.arrayBuffer();
    var res = await this._rpc('openTarget', { name: file.name, bytes: buf, password: password || '' });
    if (res.ok) { this.target = res; this.targetFile = file; this._targetPw = password || ''; }
    else { this.target = null; }
    return res;
  };

  Builder.prototype.clearTarget = async function () {
    this.target = null;
    this.targetFile = null;
    this._targetPw = '';
    if (this._worker) { try { await this._rpc('clearTarget', {}); } catch (e) {} }
  };

  // Changing the selection re-creates the worker (a WORKERFS mount cannot be
  // replaced in place), which would silently drop a loaded target archive.
  Builder.prototype._restoreTarget = async function () {
    if (!this.targetFile) return;
    var buf = await this.targetFile.arrayBuffer();
    var res = await this._rpc('openTarget', { name: this.targetFile.name, bytes: buf, password: this._targetPw || '' });
    this.target = res.ok ? res : null;
  };

  Builder.prototype.cancel = function () {
    // callMain() is synchronous inside the worker; terminate is the only way to
    // stop it, and it frees the whole module heap as a side effect.
    if (!this._worker) return false;
    var ids = Object.keys(this._pending);
    for (var i = 0; i < ids.length; i++) {
      var e = new Error('cancelled'); e.code = 'CANCELLED';
      this._pending[ids[i]].reject(e); delete this._pending[ids[i]];
    }
    this._worker.terminate();
    this._worker = null;
    this._mountedFor = null;
    return true;
  };

  Builder.prototype.reset = async function () {
    if (this._worker) { this._worker.terminate(); this._worker = null; }
    this._pending = Object.create(null);
    this._mountedFor = null;
    this._mountInfo = null;
  };

  /* The input costs no memory (WORKERFS), but on the MEMFS lane the finished
   * archive is built in the worker heap and then handed over — so THAT is what
   * can run out. A 1.9 GB ZIP completed fine on an 8 GB machine in testing, so the
   * line is drawn from device memory rather than at some round number. One owner:
   * the creator's «heavy» warning and the worker's «disk refused → memory?»
   * decision read the same number. */
  function memoryCeiling() {
    var gb = navigator.deviceMemory || 0;
    if (!gb) return 1.5 * 1024 * 1024 * 1024;   // unknown (Firefox/Safari) — assume a desktop
    return Math.max(600 * 1024 * 1024, gb * 0.25 * 1024 * 1024 * 1024);
  }

  Builder.prototype.totalBytes = function () {
    return this.entries.reduce(function (s, e) { return s + e.size; }, 0);
  };

  window.ArchiveBuilder = {
    create: function () { return new Builder(); },
    collectFromDataTransfer: collectFromDataTransfer,
    collectFromSnapshot: collectFromSnapshot,
    mergeEntries: mergeEntries,
    entryFor: entryFor,
    commonPrefix: commonPrefix,
    normPath: normPath,
    isJunk: function (p) { return JUNK_RE.test(p); },
    FORMATS: FORMATS,
    LEVELS: LEVELS,
    fmtBytes: fmtBytes,
    validatePassword: validatePassword,
    ZIP_PW_MAX: ZIP_PW_MAX,
    memoryCeiling: memoryCeiling,
  };
})();
